import type { FC, ReactNode } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type store from '@/store';
import { type RootState } from '@/components/WellLoggingOperations/types';
import { useIsCorrelation } from '@/components/WellLoggingOperations/utils';

interface ActiveTemplateDispatchContext {
    setActiveTemplateId: (templateId: number) => void;
    setDefaultTemplateId: (templateId: number) => void;
    setIsCorrelation: (state: boolean) => void;
    apply: () => void;
    reset: () => void;
}
interface ActiveTemplateProviderProps {
    children: ReactNode;
}

const ActiveTemplateContext = React.createContext<{
    activeTemplateId: number | null;
    defaultTemplateId: number | null;
    isCorrelation: boolean;
}>({ activeTemplateId: null, defaultTemplateId: null, isCorrelation: false });

const ActiveTemplateDispatchContext = React.createContext<ActiveTemplateDispatchContext>({
    setActiveTemplateId: () => null,
    setDefaultTemplateId: () => null,
    setIsCorrelation: () => null,
    apply: () => null,
    reset: () => null,
});

/*
    Контекст нужен для локальной работы с активным шаблоном (его id).
    Таким образом, пока открыто модальное окно с шаблонами, внутри можно безопасно устанавливать шаблон в состояние активного.

    Но при закрытии модального окна будет вызван метод setGlobalActiveTemplateId, который установит
    активный шаблон "глобально" - запустив последующую цепочку бизнес логики, которая опирается на активный шаблон (id)
*/

/*
    TODO: защитить от лишних render-s, которые снаружи трогают этот контекст
*/
export const ActiveTemplateProvider: FC<ActiveTemplateProviderProps> = props => {
    const { children } = props;

    /*
        Шаблон по умолчанию
    */
    const defaultTemplateId = useSelector((state: RootState) => state.templates.defaultTemplateId);
    /*
        Последний примененный шаблон
    */
    const activeTemplateId = useSelector((state: RootState) => state.templates.activeTemplateId);
    const dispatch = useDispatch<typeof store.dispatch>();
    const { setCurrentWellsTemplateId } = dispatch.templateWells;
    const { setActiveTemplateId, setDefaultTemplateId } = dispatch.templates;
    const { setForceUpdateState } = dispatch.displaySettings;

    const { activeGroupName } = useSelector((state: RootState) => state.tablets);
    const isCorr = useIsCorrelation();

    const [localDefaultTemplateId, setLocalDefaultTemplateId] = useState(defaultTemplateId);
    const [localActiveTemplateId, setLocalActiveTemplateId] = useState(activeTemplateId);
    const [localIsCorrelation, setLocalIsCorrelation] = useState<boolean>(false);

    useEffect(() => {
        setLocalIsCorrelation(isCorr);
    }, [isCorr]);

    const handleActiveTemplateId = useCallback((templateId: number) => {
        setLocalActiveTemplateId(prevID => {
            return prevID === templateId ? prevID : templateId;
        });
    }, []);

    const handleDefaultTemplateId = useCallback((templateId: number) => {
        setLocalDefaultTemplateId(prevID => {
            return prevID === templateId ? prevID : templateId;
        });
    }, []);

    const setGlobalActiveTemplateId = useCallback(() => {
        if (localActiveTemplateId === activeTemplateId) return;
        setActiveTemplateId(localActiveTemplateId);
    }, [localActiveTemplateId, activeTemplateId, setActiveTemplateId]);

    const setGlobalDefaultTemplateId = useCallback(() => {
        if (localDefaultTemplateId === defaultTemplateId) return;
        setDefaultTemplateId(localDefaultTemplateId);
    }, [localDefaultTemplateId, defaultTemplateId, setDefaultTemplateId]);

    const setGlobalIsCorrelation = useCallback(() => {
        if (activeGroupName) {
            dispatch.lists.setCorrelation({ listKey: activeGroupName, isCorrelation: localIsCorrelation });
        }
    }, [activeGroupName, localIsCorrelation]);

    const resetLocalActiveTemplateId = useCallback(() => {
        setLocalActiveTemplateId(activeTemplateId);
    }, [activeTemplateId]);

    const apply = () => {
        if (localActiveTemplateId || localActiveTemplateId === null) {
            setCurrentWellsTemplateId(localActiveTemplateId);
        }

        setForceUpdateState(true);
        /*
            Установка в глобальный store информации по выбранным шаблонам
        */
        setGlobalActiveTemplateId();
        setGlobalDefaultTemplateId();
        setGlobalIsCorrelation();
    };

    const dispatchMethods = {
        setDefaultTemplateId: handleDefaultTemplateId,
        setActiveTemplateId: handleActiveTemplateId,
        setIsCorrelation: setLocalIsCorrelation,
        reset: resetLocalActiveTemplateId,
        apply,
    };

    return (
        <ActiveTemplateContext.Provider
            value={{
                activeTemplateId: localActiveTemplateId,
                defaultTemplateId: localDefaultTemplateId,
                isCorrelation: localIsCorrelation,
            }}
        >
            <ActiveTemplateDispatchContext.Provider value={dispatchMethods}>
                {children}
            </ActiveTemplateDispatchContext.Provider>
        </ActiveTemplateContext.Provider>
    );
};

export const useTemplatesId = () => React.useContext(ActiveTemplateContext);
export const useActiveTemplateDispatch = () => React.useContext(ActiveTemplateDispatchContext);
